import React, { useEffect } from 'react';
import { Grid, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { billRateFlat, billRateHourly } from 'app/models/Placement/PlacementMods';
import { isNumber } from '../../ModificationsUtils';
import { useSelector } from 'react-redux';
import { selectIsEditMod } from 'store/redux-store/placement-mods/selectors';
import { validDecimal } from 'app/helpers/numberHelper';
import { usePromiseTracker } from 'react-promise-tracker';

export const FlatHour = ({ isExpanded, contractType }: { isExpanded: boolean; contractType: number }) => {
  const { t } = useTranslation();
  const { control, setValue, setError, clearErrors } = useFormContext();
  const isModEdit = useSelector(selectIsEditMod);
  const billRateFlatValue = useWatch<number>({ name: billRateFlat });
  const billRateHourlyValue = useWatch<number>({ name: billRateHourly });
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });

  useEffect(() => {
    if (!billRateFlatValue) {
      setError(billRateFlat, { type: 'required' });
    } else {
      clearErrors(billRateFlat);
    }
    if (!billRateHourlyValue) {
      setError(billRateHourly, { type: 'required' });
    } else {
      clearErrors(billRateHourly);
    }
  }, []);

  return (
    <Grid>
      <Grid style={{ marginTop: '15px' }}>
        <Controller
          name={billRateFlat}
          control={control}
          rules={{ required: contractType === 1 }}
          render={({ field: { onChange, ...rest } }) => (
            <TextField
              value={billRateFlatValue}
              sx={{ width: isExpanded ? '300px' : '216px' }}
              InputLabelProps={{
                shrink: billRateFlatValue ? true : false,
              }}
              variant="filled"
              label={`${t(`placement.placementMods.${billRateFlat}`)}`}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(billRateFlat, e.target.value);
                  onChange(e.target.value);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit || isSaveModApiCalling ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {isNumber(billRateFlatValue) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
      <Grid style={{ marginTop: '20px' }}>
        <Controller
          name={billRateHourly}
          control={control}
          rules={{ required: contractType === 1 }}
          render={({ field: { onChange, ...rest } }) => (
            <TextField
              value={billRateHourlyValue}
              sx={{ width: isExpanded ? '300px' : '216px' }}
              InputLabelProps={{
                shrink: billRateHourlyValue ? true : false,
              }}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(billRateHourly, e.target.value);
                  onChange(e.target.value);
                }
              }}
              variant="filled"
              label={`${t(`placement.placementMods.${billRateHourly}`)}`}
              InputProps={{
                startAdornment: (
                  <>
                    {isNumber(billRateHourlyValue) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                readOnly: !isModEdit,
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
